import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import {
  useAddOrUpdateVolunteersManagementScreenFilterMutation,
  useDeleteManagementFiltersMutation,
  useGetVolunteersListQuery,
  useGetVolunteersManagementFiltersQuery,
  useImportFromFileMutation,
  useUpdateVolunteerMutation,
} from "features/api/usersPermissionsManagement/usersPermissionsManagementApiSlice";
import { useTranslation } from "react-i18next";
import { Download, PencilSquare } from "react-bootstrap-icons";
import { Spinner, Stack } from "react-bootstrap";
import BSButton from "react-bootstrap/Button";
import { toast } from "react-toastify";
import {
  ERROR_MESSAGE,
  FilterType,
  UserRole,
} from "components/Reports/reportAssets/assets";
import MultiFilterContainer from "components/MultiFilterContainer/MultiFilterContainer";
import useFilterOptions from "components/MultiSelect/useFilterOptions";
import RenderList from "../RenderList";
import { formatObjetToNeededType, volunteerColumns } from "./volunteerUtils";
import {
  UserApp,
  VolunteersListQueryBody,
  volunteerData,
  volunteersDataTable,
} from "./volunteerTypes";
import EditModal from "./EditModal";
import { TabTypes } from "../../UsersPermissionsManagementTypes";
import CustomPagination from "../../../../../common/components/Pagination/CustomPagination";

type verifyUsersResult = {
  updateSuccess: boolean;
  updatedUsers: number;
  errorMessage: string;
};

const Volunteers: FC = () => {
  const { t } = useTranslation();
  const fileInput = useRef(null);
  const [isLoadingUploadFile, setIsLoadingUploadFile] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [editModalData, setEditModalData] = useState<UserApp | null>(null);
  const [registrationDate, setRegistrationDate] = useState();
  const [lastReportUpdate, setLastReportUpdate] = useState();

  const {
    filtersOptions,
    filterOptionInit,
    setDataToFiltersOptions,
    runFilter,
    setRunFilter,
    setFiltersOptions,
    chosenFilter,
    setChosenFilter,
    checkForSavedData,
    isOpenSavedFilter,
    closeSavedFilterComponent,
    toggleOpenSavedFilter,
    toggleOpenMultiSelect,
    openMultiSelect,
    isOpenMultiSelect,
    setIsOpenMultiSelect,
    setIsOpenSavedFilter,
  } = useFilterOptions(FilterType.volunteersType);
  const [addOrUpdateFilter, { isLoading: isSaving }] =
    useAddOrUpdateVolunteersManagementScreenFilterMutation();
  const {
    data: savedFilters,
    isLoading: isLoadingSavedFilters,
    isError: isErrorSavedFilters,
  } = useGetVolunteersManagementFiltersQuery();
  const [deleteFilter, { isLoading: isDeleting, isErrorDelete, onSuccess }] =
    useDeleteManagementFiltersMutation();
  // TODO:move to useFilterOPtions- setDatesStates
  const setDatesStates = (savedFilterDataForDates) => {
    if (savedFilterDataForDates.registrationDate) {
      setRegistrationDate((prevReportDate: any) => {
        const savedReportDate = checkForSavedData(
          "registrationDate",
          savedFilterDataForDates
        );
        return savedReportDate || prevReportDate;
      });
    }
    if (savedFilterDataForDates.lastReportUpdate) {
      setLastReportUpdate((prevUpdateDate: any) => {
        const savedUpdateDate = checkForSavedData(
          "lastReportUpdate",
          savedFilterDataForDates
        );

        return savedUpdateDate || prevUpdateDate;
      });
    }
  };

  const setDatatoFilter = useCallback(() => {
    setDataToFiltersOptions(chosenFilter);
  }, [chosenFilter, setDataToFiltersOptions]);
  useEffect(() => {
    if (Object.keys(chosenFilter).length) {
      setDatatoFilter();
      setDatesStates(chosenFilter);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chosenFilter]);

  const params = {
    multiSelect: {
      filterType: FilterType.volunteersType,
      registrationDate,
      setRegistrationDate,
      lastReportUpdate,
      setLastReportUpdate,
      filtersOptions,
      setFiltersOptions,
      setDataToFiltersOptions,
      filterOptionInit,
      runFilter,
      setRunFilter,
      setChosenFilter,
      chosenFilter,
      dbRequest: {
        addOrUpdateFilter,
        isSaving,
      },
    },
    SavedFilters: {
      filterType: FilterType.volunteersType,
      setChosenFilter,
      chosenFilter,
      openTheMultiCollapseFormOutSide: openMultiSelect,
      closeSavedFilterComponent,
      runFilter,
      setRunFilter,
      setRegistrationDate,
      setLastReportUpdate,
      filterOptionInit,
      dbRequest: {
        savedFilters,
        isLoading: isLoadingSavedFilters,
        isError: isErrorSavedFilters,
      },
      deleteDbRequest: {
        deleteFilter,
        isDeleting,
      },
    },
  };

  const [volunteersListQueryBody, setVolunteersListQueryBody] =
    useState<VolunteersListQueryBody>({});

  const submitUsersFilter = (filtersOptionsProps) => {
    if ("options" in filtersOptionsProps) {
      const formatedObject: VolunteersListQueryBody =
        formatObjetToNeededType(filtersOptionsProps);
      setVolunteersListQueryBody(formatedObject);
    } else {
      setVolunteersListQueryBody(filtersOptionsProps);
    }
  };

  const { data: volunteers }: { data: volunteersDataTable } =
    useGetVolunteersListQuery({
      page: currentPage,
      body: volunteersListQueryBody,
    });

  const [verifyUsersFromFile] = useImportFromFileMutation();
  const [updateVolunteer] = useUpdateVolunteerMutation();

  const handleUploadFile = async (event) => {
    setIsLoadingUploadFile(true);
    const file = event.target.files[0];
    if (file) {
      const formData = new FormData();
      formData.append("file", file);

      try {
        const res: verifyUsersResult = await verifyUsersFromFile(
          formData
        ).unwrap();
        setIsLoadingUploadFile(false);
        fileInput.current.value = null; // reset the input to allow another request with same file (refers to reduxTK - cache)
        res?.updateSuccess &&
          toast.success(
            `${res?.updatedUsers} ${t("volunteersSuccessfullyUpdated")}`,
            { style: { direction: "rtl" } }
          );
        !res?.updateSuccess &&
          toast.error(res?.errorMessage, { style: { direction: "rtl" } });
      } catch (error) {
        console.error("Error:", error);
        toast.error(ERROR_MESSAGE);
      }
    }
  };

  const handleUpdate = async (userAppData: UserApp) => {
    const payload: UserApp = {
      ...userAppData,
      isVerifyByMimshalZamin: userAppData.isVerifyByMimshalZamin,
    };

    await updateVolunteer(payload).unwrap();
    setEditModalData(null);
  };

  const editAction = {
    title: "",
    dataIndex: 17,
    key: "actions",
    flex: 1,
    renderCell: (volunteer: volunteerData) => {
      return (
        <Stack direction="horizontal" gap={0}>
          <div
            className={`button-wrapper ${
              volunteer?.userApp?.disable ? "disabled" : ""
            }`}
          >
            <BSButton key={volunteer?.userApp?.id} variant="outline">
              <PencilSquare
                size={20}
                color="primary"
                onClick={() => setEditModalData(volunteer.userApp)}
              />
            </BSButton>
          </div>
        </Stack>
      );
    },
    maxWidth: 80,
  };

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  return (
    <div className="tab-list-container">
      <div className="tab-list-header">
        <MultiFilterContainer
          isFollowUp={false}
          role={UserRole.CONTROLCENTER}
          isOpenMultiSelect={isOpenMultiSelect}
          isOpenSavedFilter={isOpenSavedFilter}
          setIsOpenMultiSelect={setIsOpenMultiSelect}
          setIsOpenSavedFilter={setIsOpenSavedFilter}
          params={params}
          toggleOpenMultiSelect={toggleOpenMultiSelect}
          toggleOpenSavedFilter={toggleOpenSavedFilter}
          filterType={FilterType.volunteersType}
          submitFilter={submitUsersFilter}
        />
        <div>
          <button
            type="button"
            className="downloadButton"
            onClick={() => fileInput.current.click()}
            disabled={isLoadingUploadFile}
          >
            {isLoadingUploadFile ? (
              <Spinner animation="border" variant="secondary" />
            ) : (
              <>
                <Download size={24} />
                {t("importFromFile")}
              </>
            )}
          </button>
          <input
            type="file"
            accept=".xlsx, .xls"
            ref={fileInput}
            style={{ display: "none" }}
            onChange={handleUploadFile}
          />
        </div>
      </div>
      <RenderList
        data={volunteers?.volunteersData || []}
        columns={[...volunteerColumns, editAction]}
        type={"volunteers" as TabTypes}
      />
      {!!editModalData && (
        <EditModal
          isOpen={!!editModalData}
          setIsOpen={setEditModalData}
          submitForm={handleUpdate}
          initialValues={editModalData}
        />
      )}
      {volunteers?.volunteersData?.length > 0 && (
        <div className="pagination">
          <CustomPagination
            totalPages={volunteers?.pageCount}
            activePage={currentPage}
            onPageChange={handlePageChange}
          />
        </div>
      )}
    </div>
  );
};

export default Volunteers;
